import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../../theme";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { ToastContainer, toast } from "react-toastify";
import {
  fetchAllRoleData,
  fetchClientNameData,
  fetchOrgClientWiseData,
  addRoleData,
} from "../../../../services/apiService";
import Topbar from "../../../../scenes/global/Topbar";

const GyanniMstRole = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [mstRoleMap, setMstRole] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [offset, setOffset] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedParentVal, setSelectedParentVal] = useState("");
  const [organizationData, setOrganizationData] = useState([]);

  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const [filterModel, setFilterModel] = useState({
    items: [],
  });

  const clientid = localStorage.getItem("clientId");
  const mstorgnhierarchytypeid = localStorage.getItem("organizationid");

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        // First API call to get the total number of records
        if (!clientid || !mstorgnhierarchytypeid) {
          throw new Error(
            "Missing clientid or mstorgnhierarchytypeid in localStorage"
          );
        }

        // Prepare the data for the first request
        const totalRecordsParams = {
          clientid: Number(clientid),
          organizationid: Number(mstorgnhierarchytypeid),
          limit: 100, // You can modify this limit if necessary
        };

        // First request to get the total count of records
        const initialData = await fetchAllRoleData(totalRecordsParams);
        const total = initialData?.details?.total;

        if (total === 0) {
          setMstRole([]); // If no records found, set empty array
          return;
        }

        setTotalRecords(total);

        const secondRequestParams = {
          clientid: Number(clientid),
          organizationid: Number(mstorgnhierarchytypeid),
          limit: total,
        };

        const secondData = await fetchAllRoleData(secondRequestParams);

        if (secondData && secondData.details && secondData.details.values) {
          const formattedData = secondData.details.values.map((userRole) => ({
            id: userRole.id,
            clientidname: userRole.clientidname,
            organizationname: userRole.organizationname,
            rolename: userRole.rolename,
            roledesc: userRole.roledesc,
            isadmin: userRole.isadmin === 1 ? "Yes" : "No",
          }));
          setMstRole(formattedData);
        } else {
          throw new Error("Invalid data structure from second API call");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to fetch user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleFilterChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const openModal = () => {
    setIsModalOpen(true);
    fetchClientNames();
  };

  const fetchClientNames = async () => {
    const loadingToastId = toast.loading("Loading client data...");

    try {
      const response = await fetchClientNameData({});
      if (response.success) {
        setClients(response.details || []);
        toast.update(loadingToastId, {
          render: "Client data loaded successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      } else {
        toast.update(loadingToastId, {
          render: "Error: Invalid response structure",
          type: "error",
          isLoading: false,
          autoClose: 2000,
        });
      }
    } catch (err) {
      console.error("Error fetching client name data:", err);
      toast.update(loadingToastId, {
        render: "Error fetching client name data.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const handleClientSelect = (e) => {
    const clientId = e.target.value;
    setSelectedClient(clientId);
    if (clientId) fetchClientNames(clientId);
  };

  const handleOrgClientWise = async () => {
    const loadingToastId = toast.loading("Fetching organization data...");
    try {
      if (!selectedClient) {
        toast.update(loadingToastId, {
          render: "Please select a client first.",
          type: "error",
          isLoading: false,
          autoClose: 2000,
        });
        return;
      }

      const clientId =
        typeof selectedClient === "string"
          ? parseInt(selectedClient, 10)
          : selectedClient;

      const response = await fetchOrgClientWiseData({
        clientId: clientId,
      });

      if (response.success) {
        setOrganizationData(response.details || []);
        toast.update(loadingToastId, {
          render: "Organization data loaded successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      } else {
        toast.update(loadingToastId, {
          render: "Error: Invalid response structure",
          type: "error",
          isLoading: false,
          autoClose: 2000,
        });
      }
    } catch (err) {
      console.error("Error fetching organization data:", err);
      toast.update(loadingToastId, {
        render: "Error fetching organization data.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const handleInsertRoleData = async (e) => {
    e.preventDefault();
    const requestedData = {
      clientid: Number(selectedClient),
      organizationid: Number(selectedParentVal),
      rolename: roleName,
      roledesc: roleDescription,
      isadmin: isAdmin ? 1 : 0,
    };

    try {
      const response = await addRoleData(requestedData);
      if (response.success) {
        toast.success("MST Role Added Successfully!");
      } else {
        toast.error(response?.message || "Failed to Add Role");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleEdit = () => {
    console.log("Editing client:", currentRow);
  };

  const handleDelete = () => {
    console.log("Deleting client:", currentRow);
  };

  const columns = [
    {
      field: "clientidname",
      headerName: "Client Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "organizationname",
      headerName: "Organization Name",
      flex: 1,
    },
    {
      field: "rolename",
      headerName: "Role Name",
      flex: 1,
    },
    {
      field: "roledesc",
      headerName: "Role Description",
      flex: 1,
    },
    {
      field: "isadmin",
      headerName: "Is Admin",
      flex: 1,
      renderCell: ({ value }) => <span>{value}</span>,
    },

    {
      field: "action",
      headerName: "Action",
      renderCell: ({ row }) => (
        <div style={{ display: "flex" }}>
          <IconButton
            onClick={() => handleEdit(row.original)}
            style={{ color: "#6870fa", fontSize: "12px" }}
          >
            <BorderColorIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(row.original)}
            style={{ color: "red", fontSize: "12px" }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mt-4">
        <Topbar />
      </div>

      <Box m="20px" style={{ backgroundColor: colors.primary[400] }}>
        <div className="p-4 flex items-center justify-between">
          <h6 className="font-semibold mb-4">Role Details </h6>
          <div
            style={{ backgroundColor: colors.blueAccent[500] }}
            className="text-white px-4 py-2 rounded-sm"
          >
            <button onClick={openModal} className="font-medium">
              {" "}
              <AddIcon /> Add
            </button>
          </div>
        </div>

        <Box
          m="0 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        >
          <DataGrid
            checkboxSelection
            rows={mstRoleMap}
            columns={columns}
            pageSize={pageSize}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            pagination
            loading={loading}
            filterModel={filterModel}
            onFilterModelChange={handleFilterChange}
          />
        </Box>

        {/* // add client modal pop up  */}
        <div
          className={`fixed inset-0 bg-gray-500 bg-opacity-50 z-50 transition-opacity ${
            isModalOpen ? "block" : "hidden"
          }`}
        >
          <div className="flex justify-center items-center min-h-screen">
            <div className="bg-white p-6 rounded-lg shadow-lg w-6/12">
              <h2 className="text-2xl text-center font-semibold mb-4">
                Add Role
              </h2>

              <form onSubmit={handleInsertRoleData}>
                {/* Client Name */}

                <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6 mb-3">
                  <div className="mb-3">
                    <label
                      htmlFor="clientName"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Select Client
                    </label>
                    <div className="w-full max-w-sm min-w-[200px]">
                      <div className="relative">
                        <select
                          className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border border-slate-200 rounded pl-3 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md appearance-none cursor-pointer"
                          value={selectedClient}
                          onChange={(e) => setSelectedClient(e.target.value)}
                          onClick={handleClientSelect}
                        >
                          <option value="">Select Client</option>
                          {clients.map((client, index) => (
                            <option key={index} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.2"
                          stroke="currentColor"
                          className="h-5 w-5 ml-1 absolute top-2.5 right-2.5 text-slate-700"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Client Code */}
                  <div className="mb-3">
                    <label
                      htmlFor="clientCode"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Select Organization
                    </label>
                    <div className="w-full max-w-sm min-w-[200px]">
                      <div className="relative">
                        <select
                          value={selectedParentVal}
                          className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border border-slate-200 rounded pl-3 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md appearance-none cursor-pointer"
                          onClick={handleOrgClientWise}
                          onChange={(e) => setSelectedParentVal(e.target.value)}
                        >
                          <option value="">Select Parent Organization</option>
                          {organizationData.map((org, index) => (
                            <option key={index} value={org.id}>
                              {org.organizationname}
                            </option>
                          ))}
                        </select>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.2"
                          stroke="currentColor"
                          className="h-5 w-5 ml-1 absolute top-2.5 right-2.5 text-slate-700"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="mb-3">
                    <label
                      htmlFor="keyPersonEmail"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Role Name
                    </label>
                    <input
                      type="text"
                      id="keyPersonEmail"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      value={roleName}
                      name="roleName"
                      onChange={(e) => setRoleName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="keyPersonEmail"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Role Description
                    </label>
                    <input
                      type="text"
                      id="keyPersonEmail"
                      name="roleDescription"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      value={roleDescription}
                      onChange={(e) => setRoleDescription(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center">
                      <input
                        id="link-checkbox"
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        checked={isAdmin}
                        onChange={(e) => setIsAdmin(e.target.checked)}
                      />
                      <label
                        htmlFor="link-checkbox"
                        className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Is Admin
                      </label>
                    </div>
                  </div>

                  {/* Submit and Close Buttons */}
                  <div className="col-span-2 flex justify-between space-x-4">
                    <button
                      type="submit"
                      className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                    >
                      Submit
                    </button>
                    <button
                      onClick={closeModal}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-400 focus:outline-none"
                    >
                      X
                    </button>
                  </div>
                </div>
              </form>

              {/* Close button */}
            </div>
          </div>
        </div>
        <ToastContainer />
      </Box>
    </>
  );
};

export default GyanniMstRole;
